interface NavItem {
  title: string;
  href?: string;
  children?: NavItem[];
}

export default class ProductsNavTooltip {
  private targets: HTMLElement[];

  constructor() {
    this.targets = Array.from(
      document.querySelectorAll('.ist-pc .ist-js__projuctsNavItemContent')
    ) as HTMLElement[];
    this.init();
  }

  private init() {
    this.targets.forEach((rootElement) => {
      new SingleProductsNavTooltip(rootElement);
    });
  }
}

class SingleProductsNavTooltip {
  private static instanceCounter = 0;
  private instanceId: number;
  private rootElement: HTMLElement;
  private navData: NavItem[] = [];
  private maxDepth: number = 1;
  // currentPath は各階層で選択されたリンクの番号（0～）を保持する
  private currentPath: number[] = [];
  // backFocusIndex は戻る操作時にフォーカスすべき親リンクのインデックスを保持する
  private backFocusIndex: number | null = null;
  private wrapper: HTMLElement;
  private levelContainers: HTMLElement[] = [];
  private mutationObserver: MutationObserver;
  private parentScrollElement: HTMLElement | null = null;
  private boundUpdatePosition: () => void;

  constructor(rootElement: HTMLElement) {
    this.instanceId = SingleProductsNavTooltip.instanceCounter++;
    this.rootElement = rootElement;
    this.wrapper = document.createElement('div');
    this.boundUpdatePosition = this.setPosition.bind(this);
    this.mutationObserver = new MutationObserver(() => {});
    this.setup();
  }

  private setup() {
    const originalUl = this.rootElement.querySelector(
      'ul.ist-productsTop__navTooltipInner'
    );
    if (!(originalUl instanceof HTMLUListElement)) return;

    // パースしてナビゲーションデータを取得
    this.navData = this.parseUl(originalUl);
    this.maxDepth = this.getMaxDepth(this.navData);

    // ツールチップ全体のラッパー生成（初期は非表示：aria-hidden="true"）
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('ist-js__productsTopNavTooltipInner');
    this.wrapper.setAttribute('aria-hidden', 'true');

    // スクロール可能な親要素を取得
    this.parentScrollElement = this.rootElement.closest(
      '.ist-productsTop__navList'
    );

    // 各階層ごとのコンテナを作成し、固有IDを付与する
    for (let level = 1; level <= this.maxDepth; level++) {
      const levelDiv = document.createElement('div');
      levelDiv.id = `ist-products-nav-tooltip-level${level}-${this.instanceId}`;
      levelDiv.classList.add(
        'ist-js__productsTopNavTooltipItem',
        `ist-js__productsTopNavTooltipItem--level${level}`
      );
      if (level === 1) {
        levelDiv.classList.add('ist-js__productsTopNavTooltipItem--isActive');
        levelDiv.setAttribute('aria-hidden', 'false');
      } else {
        levelDiv.setAttribute('aria-hidden', 'true');
      }
      this.levelContainers.push(levelDiv);
      this.wrapper.appendChild(levelDiv);
    }

    // 初期レベル（レベル1）のレンダリング
    this.renderLevelItems(this.navData, 1);
    originalUl.insertAdjacentElement('beforebegin', this.wrapper);

    // 初期アニメーションクラス設定
    requestAnimationFrame(() => {
      this.wrapper.classList.add('ist-js__productsTopNavTooltipInner--level1');
    });

    this.attachEvents();
    this.updateTabIndex();
    this.observeOpenState();
  }

  // ul 要素をパースして NavItem 配列に変換
  private parseUl(ul: HTMLUListElement): NavItem[] {
    const items: NavItem[] = [];
    ul.querySelectorAll(':scope > li.ist-productsTop__navTooltipItem').forEach(
      (li) => {
        const link = li.querySelector('a.ist-productsTop__navTooltipLink');
        if (!link) return;
        if (link.classList.contains('ist-productsTop__navTooltipLink--back'))
          return;

        const title = link.textContent?.trim() || '';
        const href = link.getAttribute('href') || '#';
        const childUl = li.querySelector(
          ':scope > ul.ist-productsTop__navTooltipInner'
        );

        let children: NavItem[] | undefined;
        if (childUl instanceof HTMLUListElement) {
          children = this.parseUl(childUl);
        }

        items.push({
          title: title,
          href: href && href !== '#' ? href : undefined,
          children: children && children.length > 0 ? children : undefined,
        });
      }
    );
    return items;
  }

  // 再帰的にナビゲーションの最大階層を算出
  private getMaxDepth(items: NavItem[]): number {
    let maxDepth = 1;
    const checkDepth = (nodes: NavItem[], depth: number) => {
      if (depth > maxDepth) maxDepth = depth;
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          checkDepth(node.children, depth + 1);
        }
      });
    };
    checkDepth(items, 1);
    return maxDepth;
  }

  // 現在の currentPath に基づく階層の NavItem 配列を取得
  private getCurrentLevelData(): NavItem[] {
    let current: NavItem[] = this.navData;
    for (let i = 0; i < this.currentPath.length; i++) {
      const idx = this.currentPath[i];
      const item = current[idx];
      if (item && item.children) {
        current = item.children;
      } else {
        return [];
      }
    }
    return current;
  }

  /**
   * 指定された階層 (level) のナビゲーションリストをレンダリングする
   * ・レベル2以上の場合は「戻る」リンクを先頭に追加する
   *   ※ 戻るリンクには aria-expanded / aria-controls を設定（aria-controls は親コンテナの ID）
   * ・子要素があるリンクには aria-expanded と aria-controls を設定する
   * ・アクティブなコンテナ内のリンクは data-index に実際の値を、非アクティブなら "-1" をセットする
   */
  private renderLevelItems(
    items: NavItem[],
    level: number,
    parentTitle?: string
  ) {
    const levelIndex = level - 1;
    const container = this.levelContainers[levelIndex];
    container.innerHTML = '';
    const ul = document.createElement('ul');
    ul.classList.add('ist-productsTop__navTooltipInner');
    container.appendChild(ul);

    // レベル2以上の場合、戻るリンクを先頭に追加
    if (level > 1 && parentTitle) {
      const backLi = document.createElement('li');
      backLi.classList.add('ist-productsTop__navTooltipItem');
      const backA = document.createElement('a');
      backA.classList.add(
        'ist-productsTop__navTooltipLink',
        'ist-productsTop__navTooltipLink--back'
      );
      backA.setAttribute('href', '#');
      backA.textContent = parentTitle;
      backA.setAttribute('aria-label', `${parentTitle}に戻る`);
      // 戻るリンクは、子メニューを閉じ親メニューを表示するため、aria-controls で親コンテナを指定
      backA.setAttribute('aria-expanded', 'true');
      backA.setAttribute(
        'aria-controls',
        `ist-products-nav-tooltip-level${level - 1}-${this.instanceId}`
      );
      backA.dataset.index = '-1';
      ul.appendChild(backLi);
      backLi.appendChild(backA);
    }

    // 現在のコンテナがアクティブなら data-index は実際の値、非アクティブならすべて "-1" をセット
    const isActive = container.getAttribute('aria-hidden') === 'false';

    items.forEach((item, i) => {
      const li = document.createElement('li');
      li.classList.add('ist-productsTop__navTooltipItem');

      const a = document.createElement('a');
      a.classList.add('ist-productsTop__navTooltipLink');
      a.textContent = item.title;
      a.setAttribute('href', item.href || '#');

      // 子要素がある場合、aria-expanded と aria-controls を設定
      if (item.children && item.children.length > 0) {
        a.setAttribute('aria-expanded', 'false');
        a.setAttribute(
          'aria-controls',
          `ist-products-nav-tooltip-level${level + 1}-${this.instanceId}`
        );
      }
      a.dataset.index = isActive ? i.toString() : '-1';

      li.appendChild(a);
      ul.appendChild(li);
    });
  }

  private attachEvents() {
    // delegate 方式でイベントを設定
    this.wrapper.addEventListener('click', (ev) => {
      const target = ev.target as HTMLElement;
      if (target.classList.contains('ist-productsTop__navTooltipLink')) {
        const href = target.getAttribute('href');
        if (href === '#') {
          ev.preventDefault();
        }
        this.handleLinkClick(target);
      }
    });

    this.wrapper.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        const target = ev.target as HTMLElement;
        if (target.classList.contains('ist-productsTop__navTooltipLink')) {
          ev.preventDefault();
          this.handleLinkClick(target);
        }
      }
    });
  }

  /**
   * リンククリック時の処理
   * ・戻るリンクの場合は、現在の currentPath の末尾（親リンクの番号）を一時変数に保持してから pop し、
   *   updateDisplay() を実行し、再レンダリング後にその番号に対応する親リンクにフォーカスを戻す
   * ・子階層があるリンクの場合は、クリックされたリンクの aria-expanded を true に更新し、
   *   currentPath に番号を追加して次階層をレンダリングする
   */
  private handleLinkClick(link: HTMLElement) {
    if (link.classList.contains('ist-productsTop__navTooltipLink--back')) {
      if (this.currentPath.length > 0) {
        // 戻る前の親リンクの番号を保持
        const parentIndex = this.currentPath[this.currentPath.length - 1];
        this.currentPath.pop();
        // 戻る場合は、transition 終了後に正しい親リンクへフォーカスするためインデックスを保持
        this.backFocusIndex = parentIndex;
        this.updateDisplay();
      }
    } else {
      const idxStr = link.dataset.index;
      if (idxStr == null || idxStr === '-1') return;
      const idx = parseInt(idxStr, 10);
      const currentItems = this.getCurrentLevelData();
      const item = currentItems[idx];
      if (item && item.children && item.children.length > 0) {
        link.setAttribute('aria-expanded', 'true');
        this.currentPath.push(idx);
        this.updateDisplay(item.title);
      }
    }
  }

  /**
   * 現在の currentPath の末尾の番号に対応する親リンクにフォーカスを戻す
   * ※ レベル1の場合はそのまま、レベル2以上の場合は戻るリンク分 +1 して対象リンクを取得する
   */
  private focusParentLinkAt(parentIndex: number) {
    const parentLevel = this.currentPath.length + 1; // 現在表示されている親レベル
    const container = this.levelContainers[parentLevel - 1];
    if (!container) return;
    const links = container.querySelectorAll(
      '.ist-productsTop__navTooltipLink'
    );
    // レベル1の場合は index と同じ、2 以上の場合は先頭が戻るリンクのため +1
    const targetIndex = parentLevel === 1 ? parentIndex : parentIndex + 1;
    const target = links[targetIndex] as HTMLElement;
    if (target) {
      target.focus();
    }
  }

  /**
   * 現在の階層状態に応じた表示更新
   * ・新たな階層コンテンツのレンダリング、各階層コンテナのアクティブ状態・aria-hidden の更新
   * ・transition 終了後に updateTabIndex() を再実行し、適切なリンクへフォーカス移動する
   */
  private updateDisplay(parentTitle?: string) {
    const level = this.currentPath.length + 1;
    const items = this.getCurrentLevelData();
    if (items.length === 0) {
      console.warn('No items found for the current level. Skipping update.');
      return;
    }
    this.renderLevelItems(items, level, parentTitle);
    this.updateTabIndex();

    this.levelContainers.forEach((levelDiv, idx) => {
      const lvl = idx + 1;
      if (lvl === level) {
        levelDiv.classList.add('ist-js__productsTopNavTooltipItem--isActive');
        levelDiv.setAttribute('aria-hidden', 'false');
      } else {
        levelDiv.classList.remove(
          'ist-js__productsTopNavTooltipItem--isActive'
        );
        levelDiv.setAttribute('aria-hidden', 'true');
      }
    });

    requestAnimationFrame(() => {
      const previousLevelClass = Array.from(this.wrapper.classList).find(
        (cls) => cls.startsWith('ist-js__productsTopNavTooltipInner--level')
      );
      const previousLevelNum = previousLevelClass
        ? parseInt(
            previousLevelClass.replace(
              'ist-js__productsTopNavTooltipInner--level',
              ''
            ),
            10
          )
        : null;
      const isForward = previousLevelNum !== null && level > previousLevelNum;
      const transitionClass = isForward
        ? 'is-transitioning-forward'
        : 'is-transitioning-backward';

      this.wrapper.classList.add(transitionClass);
      for (let i = 1; i <= this.maxDepth; i++) {
        this.wrapper.classList.remove(
          `ist-js__productsTopNavTooltipInner--level${i}`
        );
      }
      requestAnimationFrame(() => {
        this.wrapper.classList.add(
          `ist-js__productsTopNavTooltipInner--level${level}`
        );
      });

      const handleTransitionEnd = () => {
        this.wrapper.removeEventListener('transitionend', handleTransitionEnd);
        this.wrapper.classList.remove(transitionClass);
        this.updateTabIndex();
        // 戻る操作の場合は保持しておいた backFocusIndex を使って親リンクにフォーカス、それ以外は最初のリンクへフォーカス
        if (this.backFocusIndex !== null) {
          this.focusParentLinkAt(this.backFocusIndex);
          this.backFocusIndex = null;
        } else {
          this.focusFirstLinkOfActiveLevel();
        }
      };
      this.wrapper.addEventListener('transitionend', handleTransitionEnd);
    });
  }

  /**
   * 各階層コンテナについて、アクティブなら各リンクに正しい data-index を、非アクティブならすべて "-1" をセットする
   * ※ アクティブ判定は、コンテナにクラス "ist-js__productsTopNavTooltipItem--isActive" があるかで判断する
   */
  private updateTabIndex() {
    this.levelContainers.forEach((container) => {
      const isActive = container.classList.contains(
        'ist-js__productsTopNavTooltipItem--isActive'
      );
      const links = container.querySelectorAll(
        '.ist-productsTop__navTooltipLink'
      );
      if (isActive) {
        let realIndex = 0;
        links.forEach((link) => {
          if (
            link.classList.contains('ist-productsTop__navTooltipLink--back')
          ) {
            (link as HTMLElement).dataset.index = '-1';
          } else {
            (link as HTMLElement).dataset.index = realIndex.toString();
            realIndex++;
          }
          (link as HTMLElement).setAttribute('tabindex', '0');
        });
      } else {
        links.forEach((link) => {
          (link as HTMLElement).dataset.index = '-1';
          (link as HTMLElement).setAttribute('tabindex', '-1');
        });
      }
    });
  }

  // アクティブな階層内の最初のリンクにフォーカスを移動する
  private focusFirstLinkOfActiveLevel() {
    const activeContainer = this.levelContainers.find(
      (div) => div.getAttribute('aria-hidden') === 'false'
    );
    if (activeContainer) {
      const firstLink = activeContainer.querySelector(
        '.ist-productsTop__navTooltipLink'
      );
      if (firstLink instanceof HTMLElement) {
        firstLink.focus();
      }
    }
  }

  // ルート要素のクラス変化を監視し、開閉状態に応じて aria-hidden などを更新する
  private observeOpenState() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const isOpen = this.rootElement.classList.contains(
            'ist-js__projuctsNavItemOpen'
          );
          if (isOpen) {
            this.wrapper.setAttribute('aria-hidden', 'false');
            this.setPosition();
            this.addFollowEvents();
          } else {
            this.wrapper.setAttribute('aria-hidden', 'true');
            this.currentPath = [];
            this.updateDisplay();
            this.removeFollowEvents();
            this.rootElement.style.top = '';
            this.rootElement.style.left = '';
            this.rootElement.style.position = '';
          }
        }
      });
    });
    this.mutationObserver.observe(this.rootElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // ツールチップの位置を調整する
  private setPosition() {
    const navItem = this.rootElement.closest('.ist-js__projuctsNavItem');
    if (!navItem) return;
    const rect = navItem.getBoundingClientRect();
    this.rootElement.style.position = 'fixed';
    this.rootElement.style.top = `${rect.bottom}px`;
    this.rootElement.style.left = `${rect.left}px`;
  }

  private addFollowEvents() {
    window.addEventListener('scroll', this.boundUpdatePosition);
    window.addEventListener('resize', this.boundUpdatePosition);
    if (this.parentScrollElement) {
      this.parentScrollElement.addEventListener(
        'scroll',
        this.boundUpdatePosition
      );
    }
  }

  private removeFollowEvents() {
    window.removeEventListener('scroll', this.boundUpdatePosition);
    window.removeEventListener('resize', this.boundUpdatePosition);
    if (this.parentScrollElement) {
      this.parentScrollElement.removeEventListener(
        'scroll',
        this.boundUpdatePosition
      );
    }
  }
}
